import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { ArrowLeft, Compass as CompassIcon, Lightbulb, MapPin, Sparkles, Trophy, Target, Unlock, Loader2, Camera, Volume2, VolumeX, BookOpen, PlayCircle, RotateCw, RotateCcw, Gift, Home, Star } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { speak, stop as stopTts, isSupported as ttsSupported } from "../lib/tts";
import { API } from "../lib/api";
import DOMPurify from "dompurify";
import GpsActivateButton from "../components/GpsActivateButton";
import DistanceTile from "../components/DistanceTile";
import RouteOnboarding from "../components/RouteOnboarding";
import GPSQualityIndicator from "../components/GPSQualityIndicator";
import { htmlToPlainText } from "../lib/text";
import { notifyArrival } from "../lib/haptics";

// (helpers estratti in src/lib/text.js e src/components/GPSQualityIndicator.jsx)

export default function Game() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [progress, setProgress] = useState(null);
  const [route, setRoute] = useState(null);
  const [pos, setPos] = useState(null);
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [geoErr, setGeoErr] = useState("");
  const [proximityIdx, setProximityIdx] = useState(null); // 0-based index per quest osservativa
  const [observationalOpen, setObservationalOpen] = useState(false);
  const [questOpen, setQuestOpen] = useState(false);
  const [questAnswer, setQuestAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [completed, setCompleted] = useState(null); // {points_earned, total, completion_percent, unlocked_rewards, level}
  const [outroState, setOutroState] = useState(null); // {html, completedPayload}
  const [finalQuestState, setFinalQuestState] = useState(null); // {question, type, options, description, taccuino_entries, points}
  const [finalAnswer, setFinalAnswer] = useState("");
  const [finalAnswerIdx, setFinalAnswerIdx] = useState(null);
  const watchRef = useRef(null);
  const [simulating, setSimulating] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoData, setPhotoData] = useState(null);
  const notifiedRef = useRef(false);
  const [ttsActive, setTtsActive] = useState(false);
  // Trend "fuoco/fuochino/acqua" — confronta la distanza attuale con la media degli ultimi sample
  const lastDistanceRef = useRef(null);
  const [trend, setTrend] = useState("neutral"); // 'hot' | 'warm' | 'approaching' | 'cold' | 'neutral'
  // Scroll-gate rimosso (UX child-proof): il dialog "Scopri questo luogo" apre tutto
  // su un overlay scrollabile e il bottone "Inizia la quest" è sempre attivo.
  const descScrollRef = useRef(null);
  const [descriptionRead, setDescriptionRead] = useState(false);
  // Discovery dialog: si apre AUTOMATICAMENTE dopo lo sblocco e mostra "Scopri questo luogo".
  // Il bottone "Inizia la quest" è subito attivo: no scroll-gate (i bambini non scrollano dentro i box).
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  // GPS busy state per il bottone "Attiva GPS"
  const [gpsBusy, setGpsBusy] = useState(false);
  // Flash animato "-X punti" mostrato dopo una risposta quest sbagliata.
  // - tipo "warning": pre-3° errore, mostra la penalità potenziale (i punti non sono ancora persi)
  // - tipo "penalty": al 3° errore (auto-solve), mostra i punti effettivamente persi
  const [lostFlash, setLostFlash] = useState(null); // { amount, type, key } | null
  const lostFlashTimer = useRef(null);
  const triggerLostFlash = (amount, type) => {
    if (!amount || amount <= 0) return;
    if (lostFlashTimer.current) clearTimeout(lostFlashTimer.current);
    setLostFlash({ amount, type, key: Date.now() });
    if ("vibrate" in navigator) navigator.vibrate(type === "penalty" ? [200, 80, 200] : [80]);
    lostFlashTimer.current = setTimeout(() => setLostFlash(null), 1800);
  };
  useEffect(() => () => { if (lostFlashTimer.current) clearTimeout(lostFlashTimer.current); }, []);

  // stop tts on unmount or point change
  useEffect(() => {
    return () => stopTts();
  }, []);

  // Ask Notification permission once
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Load route + progress
  const loadProgress = async () => {
    try {
      const [routeRes, progRes] = await Promise.all([
        api.get(`/routes/${id}`),
        api.get(`/routes/${id}/progress`),
      ]);
      setRoute(routeRes.data);
      setProgress(progRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
      navigate(`/routes/${id}`);
    }
  };

  // Carica il taccuino dell'utente filtrato sulla rotta corrente.
  // Chiamata all'ingresso della pagina + dopo ogni submit-extra-quest per re-idratare.
  const refreshRouteTaccuino = async () => {
    try {
      const { data } = await api.get("/me/taccuino");
      const filtered = (data?.entries || []).filter((e) => e.route_id === id);
      // Ordinati dalla più vecchia (utile per la quest finale)
      filtered.sort((a, b) => (a.unlocked_at || "").localeCompare(b.unlocked_at || ""));
      setRouteTaccuino(filtered);
    } catch { /* silenzioso: taccuino è read-only, non è critico */ }
  };

  useEffect(() => { loadProgress(); refreshRouteTaccuino(); }, [id]);  // eslint-disable-line

  // Se il progress rehidratato indica una final_quest_pending (es. utente ha
  // ricaricato la pagina a fine caccia), apri automaticamente il dialog.
  useEffect(() => {
    if (!progress?.final_quest_pending || finalQuestState || completed || outroState) return;
    const fq = progress.final_quest || {};
    setFinalQuestState({
      question: fq.question || "",
      type: fq.type || "multiple",
      options: fq.options || [],
      description: fq.description || "",
      taccuino_entries: progress.taccuino_entries || [],
      points: fq.points || 0,
    });
  }, [progress?.final_quest_pending, progress?.taccuino_entries, progress?.final_quest, finalQuestState, completed, outroState]);

  // Geolocation watch
  useEffect(() => {
    if (!("geolocation" in navigator)) {
      setGeoErr("Geolocalizzazione non supportata");
      return;
    }
    watchRef.current = navigator.geolocation.watchPosition(
      (p) => {
        setPos([p.coords.latitude, p.coords.longitude]);
        setGpsAccuracy(p.coords.accuracy ?? null);
        setGeoErr("");
      },
      (err) => setGeoErr(err.message || "Permesso GPS negato"),
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 15000 }
    );
    return () => {
      if (watchRef.current) navigator.geolocation.clearWatch(watchRef.current);
    };
  }, []);

  const current = progress?.current_point;

  // Distance to current point
  const distance = useMemo(() => {
    if (!pos || !current) return null;
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(current.lat - pos[0]);
    const dLng = toRad(current.lng - pos[1]);
    const a = Math.sin(dLat/2)**2 + Math.cos(toRad(pos[0])) * Math.cos(toRad(current.lat)) * Math.sin(dLng/2)**2;
    return 2 * R * Math.asin(Math.sqrt(a));
  }, [pos, current]);

  const isNear = useMemo(() => {
    if (distance === null || !current) return false;
    const radius = current.radius_m || 50;
    // 1) GPS preciso (≤10m): sblocco solo entro il raggio configurato (tipicamente 10m).
    //    Nessuna concessione: la precisione del GPS è sufficiente.
    if (gpsAccuracy !== null && gpsAccuracy <= 10) {
      return distance <= radius;
    }
    // 2) GPS impreciso (>10m o sconosciuto): sblocco permissivo se l'utente è
    //    molto vicino (entro 20m), per non punirlo se il segnale è scarso.
    if (distance <= 20) return true;
    return false;
  }, [distance, gpsAccuracy, current]);

  // Aggiorna trend (fuoco / fuochino / acqua) confrontando la distanza con il sample precedente.
  // Soglia 3m per filtrare il rumore GPS. Reset quando cambia il punto corrente.
  useEffect(() => {
    lastDistanceRef.current = null;
    setTrend("neutral");
  }, [current?.point_id]);

  useEffect(() => {
    if (distance === null) return;
    const prev = lastDistanceRef.current;
    if (prev === null) {
      lastDistanceRef.current = distance;
      return;
    }
    const delta = prev - distance; // positivo se ci si avvicina
    const EPS = 3; // metri
    if (delta > EPS) setTrend("hot");
    else if (delta < -EPS) setTrend("cold");
    else setTrend("warm");
    lastDistanceRef.current = distance;
  }, [distance]);

  const refreshGps = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocalizzazione non supportata dal browser");
      setGeoErr("Geolocalizzazione non supportata");
      return;
    }
    setGpsBusy(true);
    setGeoErr("");
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setPos([p.coords.latitude, p.coords.longitude]);
        setGpsAccuracy(p.coords.accuracy ?? null);
        setSimulating(false);
        toast.success(`Posizione aggiornata (±${Math.round(p.coords.accuracy || 0)}m)`);
        setGpsBusy(false);
      },
      (err) => {
        const msg = err.code === 1
          ? "Permesso GPS negato — segui la guida per riattivarlo"
          : err.code === 2
          ? "Posizione non disponibile — esci all'aperto"
          : err.code === 3
          ? "Timeout GPS — riprova"
          : (err.message || "Errore GPS");
        setGeoErr(msg);
        toast.error(msg);
        setGpsBusy(false);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
    );
  };

  // Vibrate + notify once when entering radius
  useEffect(() => {
    if (isNear && !notifiedRef.current && current) {
      notifiedRef.current = true;
      if ("vibrate" in navigator) navigator.vibrate([120, 60, 120]);
      if ("Notification" in window && Notification.permission === "granted") {
        try {
          new Notification("Sei vicino a un punto Odismap!", {
            body: `Tocca "Sblocca!" per ${current.name}`,
            icon: "https://static.prod-images.emergentagent.com/jobs/b275cdb2-c8d0-455b-9e9d-00513a1b0ead/images/0c77666369c491577109670901e09ebddd0556523dec0cb2db7452ccce6e87db.png",
          });
        } catch {}
      }
    }
    if (!isNear) notifiedRef.current = false;
  }, [isNear, current]);

  const requestHint = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/gameplay/hint", { route_id: id });
      toast.success(`Indizio ${data.hints_used}/${data.total_clues}: ${htmlToPlainText(data.clue)}`);
      await loadProgress();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  // Feedback multisensoriale: vibrazione + "ding" al primo ingresso nel raggio.
  // Si attiva solo sul fronte di salita (false → true) e si resetta col cambio tappa.
  const wasNearRef = useRef(false);
  useEffect(() => {
    if (isNear && !current?.unlocked && !wasNearRef.current) {
      wasNearRef.current = true;
      notifyArrival();
    }
    if (!isNear) {
      wasNearRef.current = false;
    }
  }, [isNear, current?.unlocked]);

  useEffect(() => {
    wasNearRef.current = false;
    setProximityIdx(null);
    setDescriptionRead(false);
  }, [current?.point_id]);

  // Onboarding — mostrato UNA volta per caccia al tesoro. DEVE essere dichiarato
  // PRIMA del useEffect di auto-discovery perché quest'ultimo lo referenzia.
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  useEffect(() => {
    if (progress && !progress.onboarding_done) setOnboardingOpen(true);
  }, [progress?.onboarding_done, progress?.progress_id]);
  const confirmOnboarding = async () => {
    try {
      await api.post(`/routes/${id}/confirm-onboarding`);
      setOnboardingOpen(false);
      await loadProgress();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  // Auto-apri il dialog "Scopri questo luogo" SOLO la prima volta dopo lo sblocco
  // di ogni tappa. NON aprirlo se l'onboarding è ancora attivo (blocca lo scroll UX).
  const discoveryShownForPointRef = useRef(null);
  useEffect(() => {
    if (!current) return;
    if (onboardingOpen) { setDiscoveryOpen(false); return; }
    const hasObs = (current?.proximity_options || []).length > 0;
    const obsPending = hasObs && !current?.observational_completed;
    // Quest principale conta come "pending" solo se non è disattivata dall'admin
    const finalPending = !!current?.quest_question && !current?.main_quest_disabled && !current?.quest_completed;
    const anyPending = obsPending || finalPending;
    if (!current?.unlocked || !anyPending) {
      setDiscoveryOpen(false);
      return;
    }
    if (discoveryShownForPointRef.current !== current.point_id) {
      discoveryShownForPointRef.current = current.point_id;
      setDiscoveryOpen(true);
    }
  }, [current?.point_id, current?.unlocked, current?.observational_completed, current?.quest_completed, current?.quest_question, current?.proximity_options, onboardingOpen]);

  const checkProximityClick = async () => {
    // NUOVO FLUSSO: lo sblocco è basato SOLO sul GPS. Nessun anti-cheat dialog.
    if (!pos) { toast.error("Posizione GPS non disponibile"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/gameplay/check-proximity", {
        route_id: id,
        lat: pos[0],
        lng: pos[1],
        proximity_answer_index: null,
        accuracy: gpsAccuracy,
      });
      if (data.unlocked) {
        toast.success(data.message || "Sei nell'area! Scopri il luogo 👇", { duration: 3500 });
        await loadProgress();
        // Se non c'è né quest osservativa né quest finale ATTIVA → auto-complete
        // (copre anche il caso in cui la quest principale sia disattivata dall'admin)
        const hasObs = (current?.proximity_options || []).length > 0;
        const mainActive = !!current?.quest_question && !current?.main_quest_disabled;
        if (!mainActive && !hasObs) {
          try {
            const { data: qd } = await api.post("/gameplay/submit-quest", { route_id: id, answer: "" });
            if (qd.correct) {
              toast.success(`+${qd.points_earned} pt 🎉`);
              await loadProgress();
            }
          } catch { /* ignora */ }
        }
      } else {
        toast.error(data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const submitObservational = async () => {
    if (proximityIdx === null) { toast.error("Seleziona una risposta"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/gameplay/submit-observational", {
        route_id: id,
        answer_index: proximityIdx,
      });
      if (data.correct) {
        toast.success(data.message || "Bravissimo! Passa alla sfida");
        setObservationalOpen(false);
        setProximityIdx(null);
        await loadProgress();
        await refresh();
        // Se la quest principale è disattivata dall'admin, saltala: auto-submit.
        // Altrimenti apri il dialog della quest finale.
        if (current?.main_quest_disabled) {
          setTimeout(() => autoAdvanceDisabledQuest(), 300);
        } else if (current?.quest_question) {
          setTimeout(() => setQuestOpen(true), 300);
        }
      } else {
        const penalty = data.wrong_quest_penalty || current?.wrong_quest_penalty || 0;
        if (penalty > 0) triggerLostFlash(penalty, "penalty");
        toast.error(data.message || `Risposta sbagliata: -${penalty} punti`);
        await loadProgress();
        await refresh();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  // Quando la quest principale è disattivata, il frontend chiama submit-quest con
  // risposta vuota: il backend riconosce main_quest_disabled=True e auto-completa
  // assegnando max_points - clue_penalty. La response guida verso extras o tappa succ.
  const autoAdvanceDisabledQuest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/gameplay/submit-quest", { route_id: id, answer: "" });
      if (data.route_completed) {
        toast.success(`+${data.points_earned} punti!`);
        finishRoute(data);
      } else if (data.final_quest_pending) {
        toast.success(`+${data.points_earned} punti!`);
        openFinalQuestDialog(data);
      } else if (data.extra_quests_pending) {
        toast.success(`+${data.points_earned} punti! Continua con le quest extra.`);
        await loadProgress();
        await refresh();
        setTimeout(() => startNextExtraQuest(), 300);
      } else {
        setTappaComplete({
          completedName: current?.name || "Tappa",
          earned: data.points_earned,
          nextOrder: (current?.order || 1) + 1,
          totalTappe: totalPoints,
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  // Helper: apre il dialog della quest finale del taccuino con i dati ritornati
  // dal backend. Chiude eventuali dialog aperti (tappa completata, quest, extra).
  const openFinalQuestDialog = (data) => {
    setTappaComplete(null);
    setExtraQuestOpen(false);
    setQuestOpen(false);
    setFinalAnswer("");
    setFinalAnswerIdx(null);
    setFinalQuestState({
      question: data.final_quest?.question || "",
      type: data.final_quest?.type || "multiple",
      options: data.final_quest?.options || [],
      description: data.final_quest?.description || "",
      taccuino_entries: data.taccuino_entries || [],
      points: data.final_quest?.points || 0,
    });
  };

  // Helper: chiude la rotta con outro + reward. Se c'è outro_message lo mostra prima
  // della schermata "Tesoro trovato", altrimenti salta direttamente al reward.
  const finishRoute = (data) => {
    const completedPayload = {
      earned: data.points_earned || 0,
      total: data.total_points || 0,
      bonus: data.time_bonus || 0,
      referral: data.referral_bonus || 0,
      completion_percent: data.completion_percent || 0,
      unlocked_rewards: data.unlocked_rewards || [],
      level: data.level || null,
    };
    const outroHtml = (data.outro_message || "").trim();
    if (outroHtml) {
      setOutroState({ html: outroHtml, completedPayload });
    } else {
      setCompleted(completedPayload);
    }
  };

  const submitFinalQuest = async () => {
    if (!finalQuestState) return;
    if (finalQuestState.type === "multiple" && finalAnswerIdx === null) {
      toast.error("Seleziona una risposta"); return;
    }
    if (finalQuestState.type === "single" && !finalAnswer.trim()) {
      toast.error("Inserisci una risposta"); return;
    }
    setBusy(true);
    try {
      const payload = { route_id: id };
      if (finalQuestState.type === "multiple") payload.answer_index = finalAnswerIdx;
      else payload.answer = finalAnswer;
      const { data } = await api.post("/gameplay/submit-final-quest", payload);
      if (data.correct) {
        toast.success(`+${data.points_earned} punti bonus!`);
        setFinalQuestState(null);
        setFinalAnswer("");
        setFinalAnswerIdx(null);
        await refresh();
        finishRoute(data);
      } else {
        toast.error(data.message || "Risposta sbagliata: consulta il taccuino e riprova");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const submitQuest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/gameplay/submit-quest", { route_id: id, answer: questAnswer });
      if (data.correct) {
        setQuestOpen(false);
        setQuestAnswer("");
        if (data.final_quest_pending) {
          toast.success(`+${data.points_earned} punti!`);
          openFinalQuestDialog(data);
        } else if (data.route_completed) {
          toast.success(`+${data.points_earned} punti!`);
          finishRoute(data);
        } else if (data.extra_quests_pending) {
          // La quest finale è passata ma ci sono extra_quests da svolgere
          toast.success(`+${data.points_earned} punti! Continua con le quest extra.`);
          await loadProgress();
          await refresh();
          // Apri automaticamente la prima extra quest pendente
          setTimeout(() => startNextExtraQuest(), 300);
        } else {
          // Mostra overlay "Tappa completata" con CTA "Procedi alla nuova tappa"
          setTappaComplete({
            completedName: current?.name || "Tappa",
            earned: data.points_earned,
            nextOrder: (current?.order || 1) + 1,
            totalTappe: totalPoints,
          });
        }
      } else {
        // Risposta sbagliata: ogni errore sottrae subito la penalità
        const penalty = data.wrong_quest_penalty || current?.wrong_quest_penalty || 0;
        if (penalty > 0) triggerLostFlash(penalty, "penalty");
        toast.error(data.message || `Risposta sbagliata: -${penalty} punti`);
        await loadProgress();
        await refresh();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  // Overlay "Tappa completata → procedi alla nuova tappa"
  const [tappaComplete, setTappaComplete] = useState(null);
  const proceedToNextTappa = async () => {
    setTappaComplete(null);
    await loadProgress();
    await refresh();
    // Scroll top per far leggere subito il nuovo indizio
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Extra Quests state (dopo la quest finale)
  const [extraQuestOpen, setExtraQuestOpen] = useState(false);
  const [extraQuestCurrent, setExtraQuestCurrent] = useState(null); // {quest_id, type, question, options}
  const [extraAnswer, setExtraAnswer] = useState("");
  const [extraAnswerIdx, setExtraAnswerIdx] = useState(null);
  const [extraTaccuinoToast, setExtraTaccuinoToast] = useState(null);
  // Lista degli indizi collezionati nel taccuino per QUESTA rotta. Alimentata dal
  // backend all'apertura e ad ogni nuovo indizio aggiunto (submit-extra-quest).
  const [routeTaccuino, setRouteTaccuino] = useState([]);
  // Popup educativo mostrato SOLO alla PRIMA voce del taccuino di questa rotta.
  const [taccuinoIntroOpen, setTaccuinoIntroOpen] = useState(false);
  // Drawer persistente che apre la lista del taccuino durante il gioco.
  const [taccuinoDrawerOpen, setTaccuinoDrawerOpen] = useState(false);
  // Callback di avanzamento (next extra / final quest / tappa completata) da eseguire
  // DOPO la chiusura del popup educativo del taccuino, per non troncare la lettura.
  const pendingAdvanceRef = useRef(null);
  const runPendingAdvance = () => {
    const fn = pendingAdvanceRef.current;
    pendingAdvanceRef.current = null;
    if (fn) setTimeout(fn, 200);
  };

  const startNextExtraQuest = () => {
    const extras = current?.extra_quests || [];
    const doneIds = new Set(current?.extra_quests_completed || []);
    const next = extras.find((q) => !doneIds.has(q.quest_id));
    if (!next) return;
    setExtraQuestCurrent(next);
    setExtraAnswer("");
    setExtraAnswerIdx(null);
    setExtraQuestOpen(true);
  };

  const submitExtraQuest = async () => {
    if (!extraQuestCurrent) return;
    if (extraQuestCurrent.type === "multiple" && extraAnswerIdx === null) {
      toast.error("Seleziona una risposta");
      return;
    }
    if (extraQuestCurrent.type === "single" && !extraAnswer.trim()) {
      toast.error("Inserisci una risposta");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/gameplay/submit-extra-quest", {
        route_id: id,
        quest_id: extraQuestCurrent.quest_id,
        answer_index: extraQuestCurrent.type === "multiple" ? extraAnswerIdx : null,
        answer: extraQuestCurrent.type === "single" ? extraAnswer : null,
      });
      if (data.correct) {
        // Precomputa la prossima extra quest ESCLUDENDO quella appena completata.
        // Non affidarsi a `current` letto post-await: quel binding è stale rispetto
        // alla nuova progress che non è ancora stata applicata al render (fix bug
        // stale-closure iter29).
        const doneNow = new Set([...(current?.extra_quests_completed || []), extraQuestCurrent.quest_id]);
        const nextExtra = (current?.extra_quests || []).find((q) => !doneNow.has(q.quest_id));
        const currentNameSnapshot = current?.name || "Tappa";
        const currentOrderSnapshot = current?.order || 1;
        const totalTappeSnapshot = totalPoints;

        // Se è il PRIMO indizio del taccuino per questa rotta → popup educativo che
        // BLOCCA l'avanzamento finché l'utente non conferma. Successivi indizi →
        // celebrazione compatta e advance immediato.
        let deferForIntro = false;
        if (data.taccuino_entry) {
          const isFirst = routeTaccuino.length === 0;
          setRouteTaccuino((prev) => [...prev, data.taccuino_entry]);
          if (isFirst) {
            deferForIntro = true;
            setTaccuinoIntroOpen(true);
          } else {
            setExtraTaccuinoToast(data.taccuino_entry);
          }
        } else {
          toast.success("Risposta corretta!");
        }
        setExtraQuestOpen(false);
        setExtraAnswer("");
        setExtraAnswerIdx(null);
        await loadProgress();
        await refresh();

        const advance = () => {
          if (data.route_completed) {
            finishRoute(data);
          } else if (data.final_quest_pending) {
            openFinalQuestDialog(data);
          } else if (data.extra_quests_pending) {
            // Usa la nextExtra precalcolata invece di ri-chiamare startNextExtraQuest
            // che leggerebbe uno snapshot di `current` stale.
            if (nextExtra) {
              setExtraQuestCurrent(nextExtra);
              setExtraAnswer("");
              setExtraAnswerIdx(null);
              setTimeout(() => setExtraQuestOpen(true), 400);
            } else {
              // fallback difensivo: se per qualche ragione non abbiamo trovato la
              // prossima, ricarichiamo e lasciamo l'utente vedere lo stato aggiornato
              setTimeout(() => startNextExtraQuest(), 400);
            }
          } else {
            setTappaComplete({
              completedName: currentNameSnapshot,
              earned: data.points_earned || 0,
              nextOrder: currentOrderSnapshot + 1,
              totalTappe: totalTappeSnapshot,
            });
          }
        };
        if (deferForIntro) {
          // Sarà eseguito quando l'utente chiuderà il popup intro (vedi handler)
          pendingAdvanceRef.current = advance;
        } else {
          advance();
        }
      } else {
        const penalty = data.wrong_quest_penalty || current?.wrong_quest_penalty || 0;
        if (penalty > 0) triggerLostFlash(penalty, "penalty");
        toast.error(data.message || `Risposta sbagliata: -${penalty} punti`);
        await loadProgress();
        await refresh();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  // Scroll top automatico ogni volta che cambia la tappa attiva (nuovo point_id)
  const lastPointRef = useRef(null);
  useEffect(() => {
    if (current?.point_id && lastPointRef.current && lastPointRef.current !== current.point_id) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    if (current?.point_id) lastPointRef.current = current.point_id;
  }, [current?.point_id]);

  // Demo: simulate being on the point (useful when testing without real GPS)
  const teleportNear = () => {
    if (!current) return;
    setSimulating(true);
    setPos([current.lat + 0.00005, current.lng + 0.00005]);
    toast.success("Posizione simulata vicino al punto", { description: "Modalità demo" });
  };

  // Photo proof handlers
  const onPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Foto troppo grande (max 4MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setPhotoData(reader.result);
    reader.readAsDataURL(file);
  };
  const sendPhoto = async () => {
    if (!photoData || !current) return;
    try {
      await api.post("/gameplay/upload-proof", {
        route_id: id,
        point_id: current.point_id,
        photo_base64: photoData,
      });
      toast.success("Foto inviata!", { description: "L'analisi AI è in corso, verrà aggiornata in pochi secondi." });
      setPhotoOpen(false); setPhotoData(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  if (!progress || !route) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin text-gold" size={36} />
      </div>
    );
  }

  const totalPoints = route.points.length;
  const doneCount = progress.point_progress.filter((p) => p.quest_completed).length;
  const pct = (doneCount / totalPoints) * 100;

  const kids = user?.kids_mode;
  const baseSize = kids ? "text-2xl" : "text-xl";

  return (
    <div className="space-y-4">
      {/* Onboarding — mostrato una sola volta per caccia al tesoro */}
      <RouteOnboarding
        open={onboardingOpen}
        onComplete={confirmOnboarding}
        onGpsActivate={refreshGps}
        gpsActive={!!pos && !geoErr}
        gpsBusy={gpsBusy}
        routeTitle={route?.title || ""}
      />

      {/* Floating "-X punti" flash sopra tutto, visibile per ~1.8s su risposta sbagliata */}
      {lostFlash && (
        <div
          key={lostFlash.key}
          className="fixed left-1/2 top-1/3 z-[100] pointer-events-none animate-lost-points-float"
          data-testid={`lost-points-flash-${lostFlash.type}`}
        >
          <div className={`px-6 py-4 rounded-3xl border-4 shadow-tactile font-display font-extrabold text-center ${
            lostFlash.type === "penalty"
              ? "bg-compass text-white border-white text-5xl"
              : "bg-white text-compass border-compass/70 text-3xl"
          }`}>
            <div className="leading-none">-{lostFlash.amount}</div>
            <div className={`mt-1 ${lostFlash.type === "penalty" ? "text-base" : "text-xs uppercase tracking-widest"}`}>
              {lostFlash.type === "penalty" ? "punti persi!" : "punti a rischio"}
            </div>
          </div>
        </div>
      )}

      {/* Floating pill del Taccuino: visibile SOLO se l'utente ha almeno un indizio
          collezionato per questa rotta. Cliccando si apre il drawer con la lista. */}
      {routeTaccuino.length > 0 && (
        <button
          type="button"
          onClick={() => setTaccuinoDrawerOpen(true)}
          className="fixed bottom-4 right-4 z-40 flex items-center gap-2 px-4 py-3 rounded-full bg-gold hover:bg-gold-hover shadow-tactile border-2 border-forest text-ink-900 font-display font-extrabold transition-transform active:scale-95"
          data-testid="taccuino-fab"
          aria-label={`Apri taccuino, ${routeTaccuino.length} indizi`}
        >
          <BookOpen size={22}/>
          <span className="text-sm">Taccuino</span>
          <span
            className="ml-0.5 min-w-6 h-6 px-1.5 rounded-full bg-forest text-white text-xs flex items-center justify-center"
            data-testid="taccuino-fab-count"
          >
            {routeTaccuino.length}
          </span>
        </button>
      )}

      {/* Top header sticky: Home + pill punti + mappa (allineato al mockup PDF) */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/")}
          className="w-14 h-14 rounded-2xl bg-white border-2 border-forest/25 flex items-center justify-center shrink-0 shadow-tactile hover:bg-forest/5 transition-all"
          data-testid="game-back-btn"
          title="Torna alla home"
        >
          <Home size={22} className="text-forest" strokeWidth={2.4}/>
        </button>
        <div className="flex-1 h-14 rounded-full bg-forest text-white flex items-center justify-center gap-2 shadow-tactile" data-testid="points-pill">
          <Star size={20} className="text-gold fill-gold" strokeWidth={2.4}/>
          <span className="font-display font-extrabold text-2xl" data-testid="current-points">{progress.points_earned}</span>
          <span className="font-display font-bold text-lg">punti</span>
        </div>
        <div className={`w-14 h-14 rounded-2xl bg-white border-2 flex items-center justify-center shrink-0 shadow-tactile ${
          gpsAccuracy === null ? "border-outline" :
          gpsAccuracy <= 10 ? "border-forest" :
          gpsAccuracy <= 30 ? "border-yellow-500" : "border-compass"
        }`} data-testid="gps-status-pill" title={gpsAccuracy === null ? "GPS in attesa" : `GPS ±${Math.round(gpsAccuracy)}m`}>
          <MapPin size={22} className={`${
            gpsAccuracy === null ? "text-ink-500" :
            gpsAccuracy <= 10 ? "text-forest" :
            gpsAccuracy <= 30 ? "text-yellow-600" : "text-compass"
          }`} strokeWidth={2.4}/>
        </div>
      </div>

      {/* Riga progresso: TAPPA X DI Y (arancione) + dots */}
      <div className="flex items-center justify-between" data-testid="tappa-progress">
        <span className="font-display font-extrabold text-sm sm:text-base text-gold uppercase tracking-wider">
          Tappa {current?.order || 1} di {totalPoints || 1}
        </span>
        <div className="flex items-center gap-1.5">
          {Array.from({ length: totalPoints || 0 }).map((_, i) => {
            const idx = i + 1;
            const done = idx < (current?.order || 1);
            const active = idx === (current?.order || 1);
            return (
              <span
                key={`tappa-dot-${idx}`}
                className={`rounded-full transition-all ${
                  active ? "w-6 h-2 bg-gold" : done ? "w-2 h-2 bg-gold/60" : "w-2 h-2 bg-outline"
                }`}
              />
            );
          })}
        </div>
      </div>

      {progress.is_replay && (
        <div className="bg-ocean-light border-2 border-ocean rounded-2xl px-3 py-2 flex items-center gap-2 shadow-tactile" data-testid="replay-badge">
          <RotateCcw size={16} className="text-ocean shrink-0"/>
          <div className="text-xs text-ink-800 font-bold">Modalità riprova — i punti non si sommano al totale</div>
        </div>
      )}

      {/* GPS error → activate button + guide (nascosto se la posizione è simulata) */}
      {(geoErr || (!pos && !simulating)) && !current?.unlocked && !simulating && (
        <div data-testid="gps-error-banner">
          {geoErr && (
            <div className="mb-2 p-3 rounded-2xl bg-red-50 border border-red-200 text-compass text-sm font-bold flex items-center gap-2">
              <Target size={16}/> {geoErr}
            </div>
          )}
          <GpsActivateButton onRequest={refreshGps} busy={gpsBusy} variant="primary" />
        </div>
      )}

      {current ? (
        <>
          {/* Tappa card — Indizio principale allineato al mockup (bianco, centrato, con eyebrow arancione) */}
          <div className="rounded-3xl bg-white border-2 border-outline/60 shadow-tactile overflow-hidden" data-testid="tappa-card">
            {/* Icona/emoji illustrazione */}
            <div className="pt-6 pb-2 flex justify-center">
              <div className="w-24 h-24 rounded-2xl bg-gold-light/60 flex items-center justify-center text-5xl" data-testid="point-emoji">
                {current.image_url ? (
                  <img src={current.image_url} alt={current.name} className="w-full h-full object-cover rounded-2xl"/>
                ) : (
                  <span aria-hidden>🌳</span>
                )}
              </div>
            </div>
            {/* Eyebrow arancione */}
            <div className="text-center text-gold font-display font-extrabold text-sm uppercase tracking-widest mt-2">
              Il tuo indizio
            </div>
            {/* Titolo forest */}
            <h2 className={`font-display font-extrabold ${baseSize} text-forest-hover leading-tight text-center px-4 mt-1`} data-testid="point-name">
              {current.name}
            </h2>
            {/* Testo indizio */}
            <div className="px-6 py-4 text-center" data-testid="clue-card">
              {current.visible_clues.length === 0 ? (
                <p className="text-ink-700 italic text-sm">Nessun indizio richiesto. Trova il punto da solo!</p>
              ) : (
                <div className="space-y-2">
                  {current.visible_clues.slice(0, 1).map((c, i) => (
                    <div
                      key={`${current.point_id}-clue-${i}`}
                      className="rich-description text-ink-800 leading-relaxed text-center text-base"
                      data-testid={`clue-html-${i}`}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(c, {
                          ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "a", "h2", "h3", "ul", "ol", "li", "blockquote"],
                          ALLOWED_ATTR: ["href", "target", "rel"],
                        }),
                      }}
                    />
                  ))}
                </div>
              )}
              {/* Pill Ascolta l'indizio */}
              {ttsSupported() && current.visible_clues.length > 0 && (
                <button
                  onClick={() => {
                    if (ttsActive) { stopTts(); setTtsActive(false); return; }
                    const txt = current.visible_clues.slice(0, 1).map(htmlToPlainText).join(". ");
                    speak(txt, { onEnd: () => setTtsActive(false) });
                    setTtsActive(true);
                  }}
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold-light hover:bg-gold/30 text-gold-hover font-display font-extrabold text-base transition-all"
                  data-testid="tts-clues-btn"
                >
                  {ttsActive ? <VolumeX size={18}/> : <Volume2 size={18}/>} {ttsActive ? "Stop" : "Ascolta l'indizio"}
                </button>
              )}
              {/* Richiedi indizio (fallback se non ancora rivelato) */}
              {current.visible_clues.length === 0 && current.total_clues > 0 && (
                <button
                  onClick={requestHint}
                  disabled={busy}
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold-light hover:bg-gold/30 text-gold-hover font-display font-extrabold text-base transition-all disabled:opacity-50"
                  data-testid="request-hint-button"
                >
                  <Lightbulb size={18}/> Richiedi indizio
                </button>
              )}
            </div>
          </div>

          {/* Distance tile con nuovo design (forest, walking icon, big number) */}
          <DistanceTile
            distance={distance}
            trend={trend}
            onSpot={isNear && !current.unlocked}
            busy={busy}
            onUnlock={checkProximityClick}
            onMissing={() => toast.info("Avvicinati al punto per sbloccarlo")}
          />

          {/* Testo helper GPS */}
          <p className="text-center text-xs sm:text-sm text-ink-500 px-6" data-testid="gps-helper-text">
            La distanza si aggiorna da sola col GPS mentre cammini. Segui i percorsi pedonali!
          </p>

          {/* Demo: simula posizione (per testing senza GPS reale) */}
          {!isNear && !current.unlocked && (
            <button onClick={teleportNear} className="w-full text-xs text-ink-500 underline" data-testid="simulate-location-btn">
              {simulating ? "GPS simulato attivo" : "🧪 Modalità demo: simula posizione"}
            </button>
          )}

          {/* 3) STATO GPS MINIMALE + SUGGERIMENTI (in fondo) */}
          <div className="rounded-2xl bg-white/70 border border-outline p-3 space-y-2" data-testid="gps-info-section">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <GPSQualityIndicator accuracy={gpsAccuracy} />
              </div>
              <button
                type="button"
                onClick={refreshGps}
                className="text-xs font-bold text-ocean hover:underline flex items-center gap-1 shrink-0"
                title="Aggiorna posizione GPS"
                data-testid="gps-refresh-btn"
              >
                <RotateCw size={12}/> Aggiorna
              </button>
            </div>
            {isNear && gpsAccuracy !== null && gpsAccuracy > 10 && distance !== null && distance > (current.radius_m || 50) && (
              <div className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-xl p-2" data-testid="gps-permissive-warning">
                ⚠️ GPS impreciso, ma sei molto vicino al punto — sblocco consentito.
              </div>
            )}
            {!isNear && gpsAccuracy !== null && gpsAccuracy > 20 && (
              <div className="text-xs text-ink-700 bg-parchment-dark border border-outline rounded-xl p-2 space-y-1" data-testid="gps-tips">
                <div className="font-bold">💡 Suggerimenti per migliorare il GPS:</div>
                <ul className="list-disc ml-4">
                  <li>Esci all&apos;aperto, lontano da edifici alti</li>
                  <li>Attendi qualche secondo: il segnale si affina</li>
                </ul>
              </div>
            )}
          </div>

          {/* CTA per riaprire il dialog "Scopri questo luogo" se l'utente lo ha chiuso */}
          {current.unlocked && (
            ((current.proximity_options || []).length > 0 && !current.observational_completed)
            || (!!current.quest_question && !current.quest_completed)
          ) && (
            <div className="p-3 rounded-2xl bg-gradient-to-br from-gold-light to-white border-2 border-gold/40 animate-bounce-in" data-testid="point-info-card">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <BookOpen size={18} className="text-gold-hover shrink-0"/>
                  <span className="text-xs uppercase tracking-widest font-bold text-ink-700 truncate">Tappa sbloccata</span>
                </div>
                <button
                  onClick={() => setDiscoveryOpen(true)}
                  className="btn-tactile !py-2 !px-4 text-xs flex items-center gap-1.5"
                  data-testid="open-discovery-btn"
                >
                  <BookOpen size={14}/> Scopri & Quest
                </button>
              </div>
            </div>
          )}

          {/* Photo-prova nascosto su richiesta utente — il backend lo supporta ancora */}
        </>
      ) : (
        <div className="text-center py-8">
          <Sparkles className="mx-auto text-gold-hover mb-2" size={48} />
          <h2 className="font-display font-extrabold text-2xl text-ink-900">Percorso completato!</h2>
        </div>
      )}

      {/* Discovery dialog "Scopri questo luogo" — si apre AUTO dopo lo sblocco.
          UX child-proof: dialog largo, no scroll interno, il bottone "Vai alla sfida"
          è sempre attivo (nessun scroll-gate). */}
      <Dialog open={discoveryOpen} onOpenChange={setDiscoveryOpen}>
        <DialogContent className="rounded-3xl bg-parchment border-outline max-w-2xl sm:max-w-2xl w-[95vw] p-5 sm:p-6" data-testid="discovery-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-ink-900 flex items-center gap-2">
              <BookOpen className="text-gold-hover" size={20}/> Scopri questo luogo
            </DialogTitle>
            <DialogDescription className="text-ink-700">{current?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* TTS toggle */}
            {ttsSupported() && current?.description && (
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    if (ttsActive) { stopTts(); setTtsActive(false); return; }
                    speak(htmlToPlainText(current.description), { onEnd: () => setTtsActive(false) });
                    setTtsActive(true);
                  }}
                  className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-full bg-gold text-ink-900 font-bold hover:bg-gold-hover"
                  data-testid="tts-description-btn"
                >
                  {ttsActive ? <VolumeX size={14}/> : <Volume2 size={14}/>} {ttsActive ? "Stop" : "Ascolta"}
                </button>
              </div>
            )}
            {/* Image */}
            {current?.image_url && (
              <div className="mx-auto max-w-xs">
                <img
                  src={current.image_url}
                  alt={current.name}
                  className="w-full rounded-2xl border border-gold/30 bg-parchment-dark"
                  style={{ aspectRatio: "4 / 5", objectFit: "cover" }}
                  data-testid="point-image"
                  onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                />
              </div>
            )}
            {/* Description */}
            {current?.description && (
              <div
                className="rich-description bg-white/60 border border-gold/20 rounded-xl p-3"
                data-testid="point-description"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(current.description, {
                    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "a", "h2", "h3", "ul", "ol", "li", "blockquote"],
                    ALLOWED_ATTR: ["href", "target", "rel"],
                  }),
                }}
              />
            )}
            {/* Video */}
            {current?.video_url && (
              <video controls className="w-full rounded-2xl bg-black" src={current.video_url} data-testid="point-video" />
            )}
            {/* CTA: la prossima quest è l'osservativa (se esiste) oppure la finale */}
            {(() => {
              const hasObs = (current?.proximity_options || []).length > 0;
              const obsDone = !!current?.observational_completed;
              const nextIsObservational = hasObs && !obsDone;
              const mainQuestActive = !!current?.quest_question && !current?.main_quest_disabled;
              const nextIsFinal = mainQuestActive && !current?.quest_completed && !nextIsObservational;
              if (nextIsObservational) {
                return (
                  <button
                    onClick={() => { setDiscoveryOpen(false); setProximityIdx(null); setObservationalOpen(true); }}
                    className="btn-tactile w-full !py-4 flex items-center justify-center gap-2 text-base mt-2"
                    data-testid="start-observational-btn"
                  >
                    <Target size={20}/> Vai alla sfida osservativa
                  </button>
                );
              }
              if (nextIsFinal) {
                return (
                  <button
                    onClick={() => { setDiscoveryOpen(false); setQuestOpen(true); }}
                    className="btn-tactile w-full !py-4 flex items-center justify-center gap-2 text-base mt-2"
                    data-testid="start-quest-btn"
                  >
                    <Target size={20}/> Vai alla sfida
                  </button>
                );
              }
              return (
                <p className="text-sm text-ink-500 italic text-center">Nessuna sfida configurata per questa tappa.</p>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Quest osservativa (multiple choice) — arriva DOPO la descrizione,
          non è più un anti-cheat gate. Ogni errore sottrae la penalità dai punti. */}
      <Dialog open={observationalOpen} onOpenChange={setObservationalOpen}>
        <DialogContent className="rounded-3xl bg-parchment border-outline max-w-lg w-[95vw]" data-testid="observational-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-ink-900" data-testid="observational-title">Quest osservativa</DialogTitle>
            <DialogDescription className="text-ink-700">Osserva il luogo e rispondi:</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-4 bg-white rounded-2xl border border-outline text-ink-900" data-testid="observational-question">
              <CompassIcon className="inline mr-2 text-gold-hover align-middle" size={18}/>
              <span
                className="rich-description font-bold"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(current?.proximity_question || "Sei sul posto?", {
                    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "a", "h2", "h3", "ul", "ol", "li", "blockquote"],
                    ALLOWED_ATTR: ["href", "target", "rel"],
                  }),
                }}
              />
            </div>
            <RadioGroup
              value={proximityIdx !== null ? String(proximityIdx) : ""}
              onValueChange={(v) => setProximityIdx(Number(v))}
              className="space-y-2"
              data-testid="observational-options"
            >
              {(current?.proximity_options || []).map((opt, i) => (
                <Label
                  key={`${current.point_id}-obs-${i}`}
                  htmlFor={`obs-opt-${i}`}
                  className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-outline cursor-pointer hover:bg-gold-light"
                  data-testid={`observational-option-${i}`}
                >
                  <RadioGroupItem value={String(i)} id={`obs-opt-${i}`} />
                  <span className="font-bold text-ink-800">{opt}</span>
                </Label>
              ))}
            </RadioGroup>
            {(current?.observational_wrong_count ?? 0) > 0 && (
              <div className="text-xs font-bold text-compass bg-red-50 border border-red-200 rounded-2xl px-3 py-2" data-testid="observational-wrong-count">
                Risposte sbagliate: {current.observational_wrong_count} · Ogni errore -{current?.wrong_quest_penalty || 0} pt
              </div>
            )}
            <button
              onClick={submitObservational}
              disabled={busy || proximityIdx === null}
              className="btn-tactile w-full disabled:opacity-50"
              data-testid="observational-submit"
            >
              {busy ? "Verifico..." : "Conferma"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quest dialog */}
      <Dialog open={questOpen} onOpenChange={setQuestOpen}>
        <DialogContent className="rounded-3xl bg-parchment border-outline">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-ink-900" data-testid="quest-modal-title">Quest sbloccata!</DialogTitle>
            <DialogDescription className="text-ink-700">Completa la sfida per guadagnare punti.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-4 bg-white rounded-2xl border border-outline text-ink-900" data-testid="quest-question-text">
              <Target className="inline mr-2 text-compass align-middle" size={18}/>
              <span
                className="rich-description font-bold"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(current?.quest_question || "", {
                    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "a", "h2", "h3", "ul", "ol", "li", "blockquote"],
                    ALLOWED_ATTR: ["href", "target", "rel"],
                  }),
                }}
              />
            </div>
            {(current?.quest_wrong_count ?? 0) > 0 && (
              <div className="text-xs font-bold text-compass bg-red-50 border border-red-200 rounded-2xl px-3 py-2 flex items-center justify-between" data-testid="quest-wrong-count">
                <span>Risposte sbagliate: {current.quest_wrong_count}</span>
                <span className="italic">Ogni errore -{current?.wrong_quest_penalty || 0} pt</span>
              </div>
            )}
            {current?.quest_type === "multiple" ? (
              <RadioGroup value={questAnswer} onValueChange={setQuestAnswer} className="space-y-2" data-testid="quest-options">
                {current.quest_options.map((opt) => (
                  <Label key={opt} className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-outline cursor-pointer hover:bg-gold-light">
                    <RadioGroupItem value={opt} />
                    <span className="font-bold text-ink-800">{opt}</span>
                  </Label>
                ))}
              </RadioGroup>
            ) : (
              <Input
                value={questAnswer}
                onChange={(e) => setQuestAnswer(e.target.value)}
                placeholder="La tua risposta..."
                className="h-12 rounded-2xl border-outline"
                data-testid="quest-input"
              />
            )}
            <button onClick={submitQuest} disabled={busy || !questAnswer.trim()} className="btn-tactile w-full" data-testid="quest-submit">
              {busy ? "Verifico..." : "Invia risposta"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tappa completata → prossima tappa (nuovo flusso con CTA esplicito e scroll top) */}
      <Dialog open={extraQuestOpen} onOpenChange={setExtraQuestOpen}>
        <DialogContent className="rounded-3xl bg-parchment border-outline max-w-lg w-[95vw]" data-testid="extra-quest-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-ink-900" data-testid="extra-quest-title">Quest extra</DialogTitle>
            <DialogDescription className="text-ink-700">Continua la tappa con questa quest aggiuntiva:</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-4 bg-white rounded-2xl border border-outline text-ink-900" data-testid="extra-quest-question-text">
              <CompassIcon className="inline mr-2 text-gold-hover align-middle" size={18}/>
              <span
                className="rich-description font-bold"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(extraQuestCurrent?.question || "", {
                    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "a", "h2", "h3", "ul", "ol", "li", "blockquote"],
                    ALLOWED_ATTR: ["href", "target", "rel"],
                  }),
                }}
              />
            </div>
            {extraQuestCurrent?.type === "multiple" ? (
              <RadioGroup
                value={extraAnswerIdx !== null ? String(extraAnswerIdx) : ""}
                onValueChange={(v) => setExtraAnswerIdx(Number(v))}
                className="space-y-2"
                data-testid="extra-quest-options"
              >
                {(extraQuestCurrent?.options || []).map((opt, i) => (
                  <Label
                    key={`${extraQuestCurrent.quest_id}-eq-${i}`}
                    htmlFor={`eq-opt-${i}`}
                    className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-outline cursor-pointer hover:bg-gold-light"
                    data-testid={`extra-quest-option-${i}`}
                  >
                    <RadioGroupItem value={String(i)} id={`eq-opt-${i}`} />
                    <span className="font-bold text-ink-800">{opt}</span>
                  </Label>
                ))}
              </RadioGroup>
            ) : (
              <Input
                value={extraAnswer}
                onChange={(e) => setExtraAnswer(e.target.value)}
                placeholder="La tua risposta..."
                className="h-12 rounded-2xl border-outline"
                data-testid="extra-quest-input"
              />
            )}
            {(() => {
              const wrongMap = current?.extra_quests_wrong || {};
              const wc = wrongMap[extraQuestCurrent?.quest_id] || 0;
              return wc > 0 ? (
                <div className="text-xs font-bold text-compass bg-red-50 border border-red-200 rounded-2xl px-3 py-2" data-testid="extra-quest-wrong-count">
                  Risposte sbagliate: {wc} · Ogni errore -{current?.wrong_quest_penalty || 0} pt
                </div>
              ) : null;
            })()}
            <button
              onClick={submitExtraQuest}
              disabled={busy || (extraQuestCurrent?.type === "multiple" ? extraAnswerIdx === null : !extraAnswer.trim())}
              className="btn-tactile w-full disabled:opacity-50"
              data-testid="extra-quest-submit"
            >
              {busy ? "Verifico..." : "Conferma"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Taccuino aggiunto — celebration overlay (mostrato dal 2° indizio in poi) */}
      <Dialog open={!!extraTaccuinoToast} onOpenChange={(v) => !v && setExtraTaccuinoToast(null)}>
        <DialogContent className="rounded-3xl bg-parchment border-outline text-center max-w-md w-[95vw] p-6" data-testid="taccuino-added-dialog">
          <DialogTitle className="sr-only">Aggiunto al taccuino</DialogTitle>
          <DialogDescription className="sr-only">Nuova voce salvata nel tuo taccuino</DialogDescription>
          <div className="flex justify-center mb-3">
            <div className="w-20 h-20 rounded-full bg-gold flex items-center justify-center shadow-tactile">
              <BookOpen className="text-ink-900" size={40} strokeWidth={2.2}/>
            </div>
          </div>
          <h2 className="font-display font-extrabold text-2xl text-forest mb-1">Aggiunto al tuo taccuino!</h2>
          <p className="text-sm text-ink-700 mb-3">Hai scoperto una nuova voce:</p>
          <div className="rounded-2xl bg-white border-2 border-gold/50 p-4 mb-4 font-display font-extrabold text-forest text-lg" data-testid="taccuino-value">
            {extraTaccuinoToast?.value}
          </div>
          <button
            onClick={() => setExtraTaccuinoToast(null)}
            className="btn-tactile w-full"
            data-testid="taccuino-close-btn"
          >
            Continua
          </button>
        </DialogContent>
      </Dialog>

      {/* Taccuino INTRO — popup educativo mostrato SOLO alla PRIMA voce del taccuino
          di questa rotta. Blocca l'avanzamento finché l'utente non conferma. */}
      <Dialog
        open={taccuinoIntroOpen}
        onOpenChange={(v) => {
          if (!v) {
            setTaccuinoIntroOpen(false);
            runPendingAdvance();
          }
        }}
      >
        <DialogContent
          className="rounded-3xl bg-parchment border-outline text-center max-w-md w-[95vw] p-6"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          data-testid="taccuino-intro-dialog"
        >
          <DialogTitle className="sr-only">Taccuino attivato</DialogTitle>
          <DialogDescription className="sr-only">Il taccuino conserva i tuoi indizi per la quest finale.</DialogDescription>
          <div className="flex justify-center mb-3 animate-bounce-in">
            <div className="w-24 h-24 rounded-full bg-gold flex items-center justify-center shadow-tactile border-4 border-forest">
              <BookOpen className="text-forest" size={54} strokeWidth={2}/>
            </div>
          </div>
          <h2 className="font-display font-extrabold text-2xl text-forest mb-1" data-testid="taccuino-intro-title">
            Hai un nuovo taccuino! 📔
          </h2>
          <p className="text-sm text-ink-700 mb-3">
            Abbiamo aggiunto il primo indizio al tuo taccuino. <strong>Conservalo bene:</strong> ti servirà alla fine della caccia per risolvere <strong>la quest finale</strong>.
          </p>
          <div className="rounded-2xl bg-white border-2 border-gold/60 p-4 mb-3 text-left">
            <div className="text-[10px] uppercase tracking-widest text-ink-500 font-bold mb-1">Primo indizio</div>
            <div className="font-display font-extrabold text-forest text-lg" data-testid="taccuino-intro-first-value">
              {routeTaccuino[routeTaccuino.length - 1]?.value || ""}
            </div>
          </div>
          <p className="text-xs text-ink-500 mb-4">
            Troverai il taccuino sempre a portata di mano: tocca il pulsante <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gold text-ink-900 font-bold"><BookOpen size={11}/> Taccuino</span> per rileggere gli indizi.
          </p>
          <button
            onClick={() => {
              setTaccuinoIntroOpen(false);
              runPendingAdvance();
            }}
            className="btn-tactile w-full"
            data-testid="taccuino-intro-continue-btn"
          >
            Ho capito, continua
          </button>
        </DialogContent>
      </Dialog>

      {/* Taccuino DRAWER: apre la lista degli indizi collezionati per questa rotta */}
      <Dialog open={taccuinoDrawerOpen} onOpenChange={setTaccuinoDrawerOpen}>
        <DialogContent
          className="rounded-3xl bg-parchment border-outline max-w-lg w-[95vw] max-h-[85vh] overflow-y-auto p-5"
          data-testid="taccuino-drawer"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-ink-900 flex items-center gap-2" data-testid="taccuino-drawer-title">
              <BookOpen className="text-gold-hover" size={22}/> Il tuo taccuino
            </DialogTitle>
            <DialogDescription className="text-ink-700">
              {routeTaccuino.length === 0
                ? "Non hai ancora indizi. Continua le quest per collezionarli!"
                : `${routeTaccuino.length} ${routeTaccuino.length === 1 ? "indizio raccolto" : "indizi raccolti"} in questa caccia.`}
            </DialogDescription>
          </DialogHeader>
          {routeTaccuino.length > 0 && (
            <ul className="space-y-2 mt-2" data-testid="taccuino-drawer-list">
              {routeTaccuino.map((e, i) => (
                <li
                  key={e.entry_id || `td-${i}`}
                  className="flex items-start gap-3 rounded-2xl bg-white border-2 border-gold/40 p-3"
                  data-testid={`taccuino-drawer-entry-${i}`}
                >
                  <div className="w-8 h-8 rounded-full bg-gold text-ink-900 font-extrabold flex items-center justify-center text-xs shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-display font-extrabold text-forest text-base">{e.value}</div>
                    {e.point_name && (
                      <div className="text-[11px] text-ink-500">da {e.point_name}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-ink-500 italic text-center mt-3">
            Il taccuino ti servirà per rispondere alla quest finale a fine caccia.
          </p>
          <button
            onClick={() => setTaccuinoDrawerOpen(false)}
            className="btn-tactile w-full mt-3"
            data-testid="taccuino-drawer-close-btn"
          >
            Chiudi
          </button>
        </DialogContent>
      </Dialog>

      {/* Quest finale del taccuino — mostrata quando tutte le tappe sono complete */}
      <Dialog open={!!finalQuestState} onOpenChange={(v) => !v && setFinalQuestState(null)}>
        <DialogContent
          className="rounded-3xl bg-parchment border-outline max-w-2xl sm:max-w-2xl w-[95vw] max-h-[92vh] overflow-y-auto p-5 sm:p-6"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          data-testid="final-quest-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-ink-900 flex items-center gap-2" data-testid="final-quest-title">
              <BookOpen className="text-gold-hover" size={22}/> Quest finale del taccuino
            </DialogTitle>
            <DialogDescription className="text-ink-700">Rileggi i tuoi indizi e risolvi l'enigma per completare la caccia.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-1">
            {/* Indizi del taccuino */}
            {(finalQuestState?.taccuino_entries || []).length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-widest text-ink-500 font-bold mb-2">I tuoi indizi</p>
                <ul className="space-y-2" data-testid="final-quest-taccuino-list">
                  {(finalQuestState?.taccuino_entries || []).map((e, i) => (
                    <li key={e.entry_id || `tacc-${i}`} className="flex items-start gap-3 rounded-2xl bg-white border-2 border-gold/40 p-3">
                      <div className="w-8 h-8 rounded-full bg-gold text-ink-900 font-extrabold flex items-center justify-center text-xs shrink-0">{i + 1}</div>
                      <div className="flex-1">
                        <div className="font-display font-extrabold text-forest text-base" data-testid={`final-taccuino-value-${i}`}>{e.value}</div>
                        {e.point_name && <div className="text-[11px] text-ink-500">da {e.point_name}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Descrizione WYSIWYG */}
            {finalQuestState?.description && (
              <div
                className="rich-description bg-white/60 border border-gold/20 rounded-xl p-3"
                data-testid="final-quest-description"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(finalQuestState.description, {
                    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "a", "h2", "h3", "ul", "ol", "li", "blockquote"],
                    ALLOWED_ATTR: ["href", "target", "rel"],
                  }),
                }}
              />
            )}
            {/* Domanda + risposta */}
            <div className="p-4 bg-white rounded-2xl border-2 border-forest/30 text-ink-900" data-testid="final-quest-question">
              <div
                className="rich-description font-bold"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(finalQuestState?.question || "", {
                    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "a", "h2", "h3", "ul", "ol", "li", "blockquote"],
                    ALLOWED_ATTR: ["href", "target", "rel"],
                  }),
                }}
              />
            </div>
            {finalQuestState?.type === "multiple" ? (
              <RadioGroup
                value={finalAnswerIdx !== null ? String(finalAnswerIdx) : ""}
                onValueChange={(v) => setFinalAnswerIdx(Number(v))}
                className="space-y-2"
                data-testid="final-quest-options"
              >
                {(finalQuestState?.options || []).map((opt, i) => (
                  <Label
                    key={`fq-opt-${i}`}
                    htmlFor={`fq-opt-${i}`}
                    className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-outline cursor-pointer hover:bg-gold-light"
                    data-testid={`final-quest-option-${i}`}
                  >
                    <RadioGroupItem id={`fq-opt-${i}`} value={String(i)} />
                    <span className="font-bold text-ink-900">{opt}</span>
                  </Label>
                ))}
              </RadioGroup>
            ) : (
              <Input
                value={finalAnswer}
                onChange={(e) => setFinalAnswer(e.target.value)}
                placeholder="Scrivi la tua risposta"
                data-testid="final-quest-answer-input"
              />
            )}
            <p className="text-xs text-ink-500 text-center">
              🎁 Superando la quest finale guadagni <strong>+{finalQuestState?.points ?? 0} punti bonus</strong>.
            </p>
            <button
              onClick={submitFinalQuest}
              disabled={busy || (finalQuestState?.type === "multiple" ? finalAnswerIdx === null : !finalAnswer.trim())}
              className="btn-tactile w-full disabled:opacity-50"
              data-testid="final-quest-submit"
            >
              {busy ? "Verifico..." : "Conferma risposta"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Messaggio conclusivo WYSIWYG mostrato prima del premio */}
      <Dialog open={!!outroState}>
        <DialogContent
          className="rounded-3xl bg-parchment border-outline max-w-2xl sm:max-w-2xl w-[95vw] max-h-[92vh] overflow-y-auto p-5 sm:p-6 text-center"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          data-testid="outro-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-ink-900 flex items-center justify-center gap-2" data-testid="outro-title">
              <Sparkles className="text-gold-hover" size={22}/> Un ultimo pensiero
            </DialogTitle>
            <DialogDescription className="sr-only">Messaggio conclusivo dell'amministratore.</DialogDescription>
          </DialogHeader>
          <div
            className="rich-description text-left bg-white/60 border border-gold/20 rounded-xl p-4 my-3"
            data-testid="outro-message"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(outroState?.html || "", {
                ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "a", "h2", "h3", "ul", "ol", "li", "blockquote"],
                ALLOWED_ATTR: ["href", "target", "rel"],
              }),
            }}
          />
          <button
            onClick={() => {
              const payload = outroState?.completedPayload;
              setOutroState(null);
              if (payload) setCompleted(payload);
            }}
            className="btn-tactile w-full mt-2"
            data-testid="outro-continue-btn"
          >
            Scopri il tuo premio →
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tappaComplete}>
        <DialogContent
          className="rounded-3xl bg-parchment border-outline text-center max-w-md w-[95vw] p-6"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          data-testid="tappa-complete-dialog"
        >
          <DialogTitle className="sr-only">Tappa completata</DialogTitle>
          <DialogDescription className="sr-only">Complimenti, hai completato la sfida. Procedi alla prossima tappa.</DialogDescription>
          <div className="flex justify-center mb-3 animate-bounce-in">
            <div className="w-24 h-24 rounded-full bg-forest flex items-center justify-center shadow-tactile">
              <Trophy className="text-gold fill-gold" size={54} strokeWidth={1.6}/>
            </div>
          </div>
          <h2 className="font-display font-extrabold text-2xl text-forest mb-1" data-testid="tappa-complete-title">
            Hai completato la sfida!
          </h2>
          <p className="text-sm text-ink-700 mb-4">
            <strong>{tappaComplete?.completedName}</strong> — <span className="text-gold-hover font-bold">+{tappaComplete?.earned ?? 0} punti</span>
          </p>
          <div className="rounded-2xl bg-white border-2 border-forest/30 p-4 mb-4">
            <div className="text-xs uppercase tracking-widest text-ink-500 font-bold mb-1">Prossima tappa</div>
            <div className="font-display font-extrabold text-forest text-lg">
              Tappa {tappaComplete?.nextOrder} di {tappaComplete?.totalTappe}
            </div>
          </div>
          <button
            onClick={proceedToNextTappa}
            className="btn-tactile w-full flex items-center justify-center gap-2 text-base"
            data-testid="proceed-next-tappa-btn"
          >
            Procedi alla nuova tappa →
          </button>
        </DialogContent>
      </Dialog>

      {/* Completed dialog */}
      <Dialog open={!!completed} onOpenChange={() => { setCompleted(null); navigate("/"); }}>
        <DialogContent className="rounded-3xl bg-parchment border-outline text-center max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-ink-900">Tesoro trovato!</DialogTitle>
            <DialogDescription className="text-ink-700">Hai completato il percorso. Ecco il riepilogo.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Sparkles className="mx-auto text-gold-hover animate-pulse" size={56} />
            <p className="text-ink-700 mt-3">Hai completato il percorso e guadagnato</p>
            <div className="text-5xl font-display font-extrabold text-gold-hover mt-2" data-testid="route-total-points">{completed?.total} punti</div>
            {completed?.completion_percent != null && (
              <div className="mt-2 text-sm font-bold text-ink-700" data-testid="completion-percent">
                {completed.completion_percent}% del punteggio massimo
              </div>
            )}
            {completed?.bonus > 0 && (
              <div className="mt-2 inline-block px-3 py-1 rounded-full bg-compass text-white font-bold text-sm" data-testid="time-bonus-badge">
                +{completed.bonus} bonus tempo!
              </div>
            )}
            {completed?.referral > 0 && (
              <div className="mt-2 inline-block px-3 py-1 ml-1 rounded-full bg-ocean text-white font-bold text-sm" data-testid="referral-bonus-badge">
                🎁 +{completed.referral} bonus invito!
              </div>
            )}

            {/* Livello globale */}
            {completed?.level?.current && (
              <div className="mt-4 mx-auto max-w-xs p-4 rounded-2xl bg-gradient-to-br from-gold-light to-white border-2 border-gold/40" data-testid="level-card">
                <div className="w-20 h-20 mx-auto rounded-2xl bg-white border-2 border-gold/40 overflow-hidden flex items-center justify-center">
                  {completed.level.current.image_url ? (
                    <img
                      src={completed.level.current.image_url}
                      alt={completed.level.current.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-5xl leading-none">{completed.level.current.emoji || "🏆"}</span>
                  )}
                </div>
                <div className="font-display font-extrabold text-lg text-ink-900 mt-2">
                  Sei {completed.level.current.name}
                </div>
                {completed.level.next && (
                  <>
                    <div className="text-[11px] text-ink-500 font-bold mt-2 flex items-center justify-center gap-1">
                      <span>Prossimo:</span>
                      {completed.level.next.image_url ? (
                        <img src={completed.level.next.image_url} alt="" className="w-4 h-4 object-cover rounded-sm"/>
                      ) : (
                        <span>{completed.level.next.emoji}</span>
                      )}
                      <span>{completed.level.next.name}</span>
                    </div>
                    <div className="mt-1.5">
                      <Progress value={completed.level.progress_pct} className="h-2"/>
                    </div>
                    <div className="text-[10px] text-ink-500 mt-1">
                      {completed.level.points} / {completed.level.next.min_points} pt
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Premi sbloccati */}
            {(completed?.unlocked_rewards || []).length > 0 && (
              <div className="mt-4 mx-auto max-w-sm space-y-2" data-testid="unlocked-rewards">
                <div className="flex items-center justify-center gap-2 text-xs uppercase tracking-widest font-bold text-ink-700">
                  <Gift size={14} className="text-compass"/> Omaggi sbloccati
                </div>
                {completed.unlocked_rewards.map((r) => (
                  <div key={r.reward_id} className="p-3 rounded-2xl bg-white border-2 border-gold text-left" data-testid={`unlocked-reward-${r.reward_id}`}>
                    <div className="font-display font-extrabold text-ink-900">{r.title}</div>
                    {r.message && <div className="text-xs text-ink-700 mt-1">{r.message}</div>}
                    {r.gift_code && (
                      <div className="mt-2 inline-block px-2 py-1 bg-gold-light border border-gold rounded-lg font-mono text-xs font-bold">
                        Codice: {r.gift_code}
                      </div>
                    )}
                    <div className="text-[10px] text-ink-500 mt-1.5">
                      {r.email_status === "ok" && "✉️ Email inviata"}
                      {r.email_status === "mocked" && "✉️ Email simulata (MOCK)"}
                      {r.email_status === "error" && "⚠️ Email non inviata"}
                      {r.email_status === "pending" && "✉️ Invio in corso..."}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap justify-center gap-2 mt-6">
              <button onClick={() => navigate("/profile")} className="btn-tactile" data-testid="completed-go-profile">Vedi profilo</button>
              <button onClick={() => navigate("/leaderboard")} className="px-4 py-2 rounded-2xl bg-white border-2 border-outline font-bold text-ink-800 hover:bg-gold-light" data-testid="completed-go-leaderboard">Classifica</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo proof dialog */}
      <Dialog open={photoOpen} onOpenChange={setPhotoOpen}>
        <DialogContent className="rounded-3xl bg-parchment border-outline">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-ink-900">Scatta una foto-prova</DialogTitle>
            <DialogDescription className="text-ink-700">
              Mostra il luogo per validare la tua presenza. Solo l'admin può vederla.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onPhotoChange}
              className="block w-full text-sm text-ink-800 file:btn-tactile file:!py-2 file:!px-4 file:mr-3"
              data-testid="photo-input"
            />
            {photoData && (
              <img src={photoData} alt="anteprima" className="w-full max-h-64 object-contain rounded-2xl border border-outline" data-testid="photo-preview" />
            )}
            <button
              onClick={sendPhoto}
              disabled={!photoData}
              className="btn-tactile w-full disabled:opacity-50"
              data-testid="photo-send-btn"
            >
              Invia foto-prova
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
